import { Features } from '@backend/lib-feature-mgr';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSessionStorage } from 'react-use';

import { MISSIONS_PER_PAGE, type SearchJobOfferOutput, searchJobOffers } from '~/business/JobOffer/searchJobOffers';
import { type BreadcrumbItemType, Breadcrumbs } from '~/components/Breadcrumbs';
import { Container } from '~/components/Container/Container';
import { IframeContainer } from '~/components/Container/iframeContainer';
import { FiltersPanel } from '~/components/FiltersPanel/FiltersPanel';
import { Head, NOINDEX_FOLLOW } from '~/components/Head';
import { Header } from '~/components/Header';
import { HomePageOffers } from '~/components/HomePageOffers';
import { LatestJobOffersCard } from '~/components/LatestJobOffersCard/LatestJobOffersCard';
import { MissionList } from '~/components/MissionsList/MissionList';
import { MissionListContainer } from '~/components/MissionsList/MissionListContainer';
import { MissionListCounter } from '~/components/MissionsList/MissionListCounter';
import { MissionListNoMissions } from '~/components/MissionsList/MissionListNoMissions';
import { MissionListPaginationBar } from '~/components/MissionsList/MissionListPaginationBar';
import { SEOContainer, SEOText, SEOTitle } from '~/components/SEOText';
import { SearchContainer } from '~/components/Welcome/styles';
import { getJobsUrl } from '~/config';
import { businessUnits } from '~/config/businessUnits';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import type { OnSubmit } from '~/contexts/FormContext/types';
import { SearchFormContextProvider, type SearchFormType } from '~/contexts/SearchFormContext/SearchFormContext';
import { EVENTS, useTrackingContext } from '~/contexts/TrackingContext';
import { businessUnitGuard } from '~/guards/businessUnitGuard';
import { useCheckFeatureEnabled, useTranslation } from '~/hooks';
import { useGenerateSearchUrl } from '~/hooks/useGenerateSearchUrl';
import { useHref } from '~/hooks/useHref';
import { useJobFiltersTrackedValues } from '~/hooks/useJobFiltersTrackedValues';
import type { CityListItem, JobListItem } from '~/lists/types';
import { getLatestJobOffers } from '~/utils/getLatestJobOffers';
import { getRootHreflangLinks } from '~/utils/hreflang';
import { getList } from '~/utils/list/getList';
import { navigateOrReload } from '~/utils/navigation';
import { ErrorWithContext } from '~/utils/search/error';
import { getDefaultParams } from '~/utils/search/getDefaultSearchParams';
import { getSearchParamsFromUrl } from '~/utils/search/getSearchParamsFromUrl';
import type { SearchParamsWithDefault } from '~/utils/search/types';

export type IndexPageProps = {
  latestJobOffers: SearchJobOfferOutput[];
  totalLatestJobOffers: number;
  // an iframe board lists the offers at its root: only a URL carrying a search is a search
  isSearchUrl: boolean;
  search: null | {
    params: SearchParamsWithDefault;
    cityListItem: CityListItem | null;
    jobListItem: JobListItem | null;
    total: number;
    jobOffers: SearchJobOfferOutput[];
    pages: number;
  };
};

const NoMissionModal = dynamic(() => import('~/components/NoMissionModal'));

const IndexPage: FC<IndexPageProps> = ({ latestJobOffers, totalLatestJobOffers, isSearchUrl, search }) => {
  const t = useTranslation();
  const { push } = useRouter();
  const { baseHref } = useHref();
  const generateSearchUrlFromHook = useGenerateSearchUrl();
  const isSEOEnabled = useCheckFeatureEnabled(Features.JobBoardSEO);
  const { businessUnit, iframe } = useBusinessUnitContext();
  const downloadAppIfNoJobOffersFeature = useCheckFeatureEnabled(Features.JobsDownloadAppIfNoJobOffers);
  const { trackPage, trackActiveMissionPageChange, trackJobOfferClicked, trackSearchResultsDisplayed } =
    useTrackingContext();

  const isSearchApplied = useMemo(() => Boolean(search), [search]);
  const [showNoMissionModal, setShowNoMissionModal] = useState(false);
  const [showFiltersPanelForm, setShowFiltersPanelForm] = useState(false);
  const [noMissionModalAlreadyShown, setNoMissionModalAlreadyShown] = useSessionStorage(
    'no_mission_modal_already_shown',
    false,
  );

  useEffect(() => {
    if (!noMissionModalAlreadyShown && search && !search.total) {
      setShowNoMissionModal(true);
      setNoMissionModalAlreadyShown(true);
    }
  }, [search?.total]);

  useEffect(() => {
    if (!isSearchApplied) {
      trackPage(EVENTS.welcomePageLoaded);
    }
  }, [isSearchApplied]);

  const scrollToMissionsList = useCallback(() => {
    const element = document.getElementById('missions_count');
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const showMissionList = useCallback(
    async ({ newParams, page }: { newParams?: SearchFormType; page?: number }) => {
      const url = generateSearchUrlFromHook({ ...search?.params, ...newParams, page });
      await navigateOrReload(push, url, { scroll: false });
      scrollToMissionsList();
    },
    [generateSearchUrlFromHook, push, scrollToMissionsList, search?.params],
  );

  const jobFiltersTrackedValues = useJobFiltersTrackedValues(search?.params, search?.total);

  useEffect(() => {
    if (isSearchApplied) {
      trackSearchResultsDisplayed(jobFiltersTrackedValues);
    }
  }, [isSearchApplied, jobFiltersTrackedValues]);

  const handleFormSubmit = useCallback<OnSubmit<SearchFormType>>(
    async newParams => {
      await showMissionList({ newParams });
      setShowFiltersPanelForm(false);
    },
    [showMissionList],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      trackActiveMissionPageChange({ ...jobFiltersTrackedValues, pageNumber: page });
      await showMissionList({ page });
    },
    [trackActiveMissionPageChange, showMissionList, jobFiltersTrackedValues],
  );

  const handleMissionClick = useCallback(
    (jobOffer: SearchJobOfferOutput) => {
      trackJobOfferClicked({ ...jobFiltersTrackedValues, refId: jobOffer.refId, jobTitle: jobOffer.title });
    },
    [jobFiltersTrackedValues],
  );

  const handleNoMissionClose = useCallback(async () => {
    setShowNoMissionModal(false);
  }, [setShowNoMissionModal]);

  const handleToggleFiltersPanelForm = useCallback(() => {
    setShowFiltersPanelForm(expanded => !expanded);
  }, [setShowFiltersPanelForm]);

  const generateMeta = useCallback(
    (metaType: string, city?: string, zipcode?: string) => {
      if (city) {
        if (zipcode) {
          return t(`${metaType}_city_zipcode`, { city, zipcode });
        }
        return t(`${metaType}_city`, { city });
      }
      return t(metaType);
    },
    [t],
  );

  const { title, description, socialTitle, socialDescription } = useMemo(
    () => ({
      description: t('welcome_page.meta_description'),
      title: generateMeta('welcome_page.meta_title', search?.params.address?.city, search?.params.address?.zipcode),
      socialDescription: generateMeta('welcome_page.meta_social_description', search?.params.address?.city),
      socialTitle: generateMeta(
        'welcome_page.meta_social_title',
        search?.params.address?.city,
        search?.params.address?.zipcode,
      ),
    }),
    [t, search?.params.address],
  );

  const hreflangLinks = useMemo(
    () => (isSearchApplied ? undefined : getRootHreflangLinks(businessUnit)),
    [isSearchApplied, businessUnit],
  );

  const getSearchPageHref = useCallback(
    (page: number) => generateSearchUrlFromHook({ ...search?.params, page }),
    [generateSearchUrlFromHook, search?.params],
  );

  const paginationLinks = useMemo(() => {
    if (!search || search.pages <= 1) {
      return undefined;
    }
    const { page } = search.params;
    return {
      prev: page > 1 ? `${getJobsUrl()}${getSearchPageHref(page - 1)}` : undefined,
      next: page < search.pages ? `${getJobsUrl()}${getSearchPageHref(page + 1)}` : undefined,
    };
  }, [search, getSearchPageHref]);

  const seo = useMemo(() => {
    if (isSEOEnabled && (search?.cityListItem || search?.jobListItem)) {
      const pageNameParts: string[] = [];

      if (search.cityListItem) {
        pageNameParts.push('city');
      }
      if (search.jobListItem) {
        pageNameParts.push('job');
      }

      return {
        title: t(`${pageNameParts.join('_')}_page.title`, {
          job: search?.jobListItem?.name,
          city: search.cityListItem?.name,
        }),
        description: search.cityListItem?.description ?? search.jobListItem?.description,
      };
    }
    return undefined;
  }, [isSEOEnabled, t, search?.cityListItem, search?.jobListItem]);

  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItemType[] = [];

    if (search?.params.address) {
      items.push({
        label: search?.params.address.name,
        href: generateSearchUrlFromHook({ address: search?.params.address }),
      });
    }

    if (search?.params.job) {
      items.push({
        label: search?.params.job,
        href: generateSearchUrlFromHook({ address: search?.params.address, job: search?.params.job }),
      });
    }

    return items;
  }, [search?.params.address, search?.params.job, generateSearchUrlFromHook, baseHref]);

  const isSearchAppliedContainer = search ? (
    <SearchContainer>
      <FiltersPanel
        showFiltersPanelForm={showFiltersPanelForm}
        onToggleFiltersPanelForm={handleToggleFiltersPanelForm}
      />
      <MissionListContainer showFiltersPanelForm={showFiltersPanelForm}>
        {Boolean(search.total) && <MissionListCounter count={search.total} />}
        <MissionList
          jobOffers={search.jobOffers}
          onMissionClick={handleMissionClick}
          showDistance={Boolean(search.params.address)}
        />
        {!search.total && <MissionListNoMissions />}
        {search.pages > 1 && (
          <MissionListPaginationBar
            pageCount={search.pages}
            currentPage={search.params.page}
            onChangePage={handlePageChange}
            getPageHref={getSearchPageHref}
          />
        )}
      </MissionListContainer>
    </SearchContainer>
  ) : null;

  return (
    <>
      <Head
        title={title}
        description={description}
        socialTitle={socialTitle}
        socialDescription={socialDescription}
        hreflangLinks={hreflangLinks}
        paginationLinks={paginationLinks}
        // a search is a query on the offers, not a page: indexing every location x job x filter combination
        // makes thousands of near-duplicates, while its links still lead crawlers to the offers
        robots={isSearchUrl ? NOINDEX_FOLLOW : undefined}
      />
      <SearchFormContextProvider
        params={search?.params}
        onSubmit={handleFormSubmit}
        defaultSubmitted={Boolean(search?.params.address) || iframe}
      >
        {iframe ? (
          isSearchApplied && <IframeContainer>{isSearchAppliedContainer}</IframeContainer>
        ) : (
          <>
            <Header isSearchApplied={isSearchApplied} title={`${t('welcome_page.title')} `} />
            {isSearchApplied && (
              <>
                <Breadcrumbs items={breadcrumbs} />
                <Container>{isSearchAppliedContainer}</Container>
                {seo ? (
                  <SEOContainer>
                    {seo.title ? <SEOTitle content={seo.title} /> : null}
                    {seo.description ? <SEOText content={seo.description} /> : null}
                  </SEOContainer>
                ) : null}
              </>
            )}
            {isSEOEnabled && <HomePageOffers totalJobOffers={totalLatestJobOffers} isSearchApplied={isSearchApplied} />}
            {!isSearchApplied && <LatestJobOffersCard latestJobOffers={latestJobOffers} hideButton />}
          </>
        )}
      </SearchFormContextProvider>
      {downloadAppIfNoJobOffersFeature && showNoMissionModal && <NoMissionModal onClose={handleNoMissionClose} />}
    </>
  );
};

export default IndexPage;

export const getServerSideProps = businessUnitGuard<IndexPageProps>(async (businessUnit, context) => {
  try {
    const { country, iframe } = businessUnits[businessUnit];

    const searchParamsFromUrl = getSearchParamsFromUrl({
      businessUnit,
      country,
      query: context.query,
      resolvedUrl: context.resolvedUrl,
    });

    // whether the user targeted a location or a job (as opposed to only flat filters like sort/type/experience)
    const hasAddressOrJob = Boolean(searchParamsFromUrl?.address || searchParamsFromUrl?.job);

    // if there are url params but no address
    if (hasAddressOrJob && !searchParamsFromUrl?.address) {
      // return 404 if the address is mandatory or if there is no job provided
      if (Features.JobBoardLocationMandatory.isEnabled(businessUnit) || !searchParamsFromUrl?.job) {
        return { notFound: true };
      }
    }

    const { latestJobOffers, totalLatestJobOffers } = await getLatestJobOffers(businessUnit);

    if (!hasAddressOrJob && !iframe) {
      return {
        props: {
          latestJobOffers,
          totalLatestJobOffers,
          isSearchUrl: false,
          search: null,
        },
      };
    }

    const defaultParams = getDefaultParams(country);
    const searchParams = { ...defaultParams, ...searchParamsFromUrl };

    let missionsPerPage = MISSIONS_PER_PAGE;

    if (Features.FreeApplications.isEnabled(businessUnit)) {
      missionsPerPage -= 1;
    }

    const { jobOffers, totalJobOffers } = await searchJobOffers({
      businessUnit,
      missionsPerPage,
      job: searchParams.job,
      type: searchParams.type,
      sort: searchParams.sort,
      page: searchParams.page,
      experience: searchParams.experience,
      ...(searchParams.address?.location
        ? {
            distance: searchParams.distance,
            latitude: searchParams.address.location.latitude,
            longitude: searchParams.address.location.longitude,
          }
        : {}),
    });

    let cityListItem: CityListItem | undefined;
    let jobListItem: JobListItem | undefined;

    const addressName = searchParams.address?.name?.toLowerCase();

    if (addressName) {
      const cityList = getList(businessUnit, 'cities');
      cityListItem = cityList.find(entry => entry.name.toLowerCase() === addressName);
    }

    const jobName = searchParams.job?.toLowerCase();

    if (jobName) {
      const jobList = getList(businessUnit, 'jobs');
      jobListItem = jobList.find(entry => entry.name.toLowerCase() === jobName);
    }

    return {
      props: {
        latestJobOffers,
        totalLatestJobOffers,
        isSearchUrl: Boolean(searchParamsFromUrl),
        search: {
          cityListItem: cityListItem || null,
          jobListItem: jobListItem || null,
          jobOffers,
          pages: Math.ceil(totalJobOffers / missionsPerPage),
          params: searchParams,
          total: totalJobOffers,
        },
      },
    };
  } catch (error) {
    // a URL that does not parse as a search is not a page; any other failure (the indexer down) must not answer a
    // 404, which would tell search engines the home page is gone
    if (error instanceof ErrorWithContext) {
      return { notFound: true };
    }
    throw error;
  }
});
